/**
 * Admin Configuration
 * 
 * Controls admin access to dashboard and management features.
 * Also manages pilot program whitelist for controlled access.
 * 
 * @module config/adminConfig
 * @see docs/ADMIN_TOOLS.md for admin access documentation
 * 
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │  GLOBAL FEATURE FLAGS QUICK REFERENCE                              │
 * │                                                                     │
 * │  Flag                      │ Value  │ Why disabled                  │
 * │  ─────────────────────────────────────────────────────────────────  │
 * │  MONETIZATION_MODE          │ affiliate │ Default to CPC/affiliate  │
 * │  RECOMMENDATION_MODE        │ rag    │ Vector similarity active     │
 * │  INHOUSE_SELLING_ENABLED    │ false  │ No warehouse/shipping ready  │
 * │  SAMPLE_FEATURE_ENABLED     │ false  │ No product inventory to ship │
 * │  PILOT_MODE_ENABLED         │ false  │ Public access allowed        │
 * │                                                                     │
 * │  Backend env vars (api/local.settings.json):                        │
 * │  SAMPLE_FEATURE_ENABLED     │ (unset)│ Must be 'true' to enable     │
 * │                                                                     │
 * │  Tests: src/config/adminConfig.test.ts (frontend)                   │
 * │         api/src/__tests__/featureFlags.test.ts (backend)            │
 * │  ⚠️  Update tests when changing any flag value!                     │
 * └─────────────────────────────────────────────────────────────────────┘
 */

// =============================================================================
// Monetization Mode Configuration
// =============================================================================

/**
 * Monetization Mode for product recommendations
 * 
 * - 'affiliate': Use Amazon affiliate products (default)
 * - 'inhouse': Use in-house catalog products
 * 
 * Users can toggle this in Settings. This constant serves as the fallback.
 * When 'affiliate' mode is enabled, Editor Picks will show Amazon products
 * with proper affiliate disclosure badges.
 */
export type MonetizationMode = 'affiliate' | 'inhouse';
export const MONETIZATION_MODE: MonetizationMode = 'affiliate';

/**
 * Recommendation Algorithm Mode
 * 
 * - 'rag': Use vector similarity (RAG) to boost product matching (default)
 * - 'traditional': Use rule-based scoring only (skin type, concerns, keywords)
 * 
 * Toggle this to compare RAG vs traditional recommendation quality.
 */
export type RecommendationMode = 'rag' | 'traditional';
export const RECOMMENDATION_MODE: RecommendationMode = 'rag';

/**
 * List of whitelisted admin email addresses.
 * Add new admin emails to this array.
 * 
 * @example
 * // To add a new admin:
 * // 1. Add email to this array
 * // 2. Restart React app
 * // 3. Admin signs in with Google using whitelisted email
 */
export const ADMIN_EMAILS: readonly string[] = [
  'dev@heseda.com',
  'cheoljin@gmail.com',
  'aivectorllc@gmail.com',
] as const;

/**
 * List of tester email addresses.
 * Testers get 10x quota (not unlimited like admins) and no admin menu access.
 */
export const TESTER_EMAILS: readonly string[] = [
  'pgcb1123@gmail.com',
  'gwonmoon.noh@gmail.com',
  'heseda.e2e.test@gmail.com',
  // Add tester emails here
] as const;

/**
 * In-House Selling Features (Cart, Checkout, Wishlist)
 * 
 * When false, hides all in-house selling UI:
 * - Cart icon and panel
 * - Checkout/payment flow
 * - Wishlist (My Picks) icon and panel
 * - "Add to Cart" buttons on product cards
 * - Monetization mode toggle in Settings
 * 
 * Set to true when warehouse shipping is ready for global orders.
 * All underlying code and hooks remain intact for easy re-enablement.
 */
export const INHOUSE_SELLING_ENABLED = false;

/**
 * Sample Shipping Feature (Request Sample, drip emails, sample status)
 * 
 * When false, hides the "Request Sample" button, prevents sample tracking
 * fields from being auto-set on address save, silences sample-related
 * greetings and AI prompts, and stops the email drip campaign.
 * 
 * Set to true when physical sample fulfillment pipeline is operational.
 * All underlying code, types, and admin views remain intact.
 */
export const SAMPLE_FEATURE_ENABLED = false;

/**
 * Pilot Program Whitelist
 * 
 * Only whitelisted emails can access the app during pilot phase.
 * This protects against unsolicited/random access for:
 * - Security reasons
 * - Idea protection
 * - Controlled testing with known users
 * 
 * Set PILOT_MODE_ENABLED to false to allow public access.
 */
export const PILOT_MODE_ENABLED = false;

export const PILOT_WHITELIST: readonly string[] = [
  // Admins & testers (automatically included)
  ...ADMIN_EMAILS,
  ...TESTER_EMAILS,
  
  // Registered Firebase users (as of Dec 22, 2025)
  // Review and remove as needed
  'bryankim527@gmail.com',
  'sjung@thecosmonature.com',
  'richargrafico@gmail.com',
  // gwonmoon.noh@gmail.com — included via ...TESTER_EMAILS spread above
  'teeoom.dev@gmail.com',
  'aznmaradona@gmail.com',
  'zappman@teeoom.com',
  'skyoon000@teeoom.com',
  // pgcb1123@gmail.com — included via ...TESTER_EMAILS spread above
  'aivector.info@gmail.com',
] as const;

/**
 * Checks if a user email is an admin.
 * 
 * @param email - User's email address (can be null/undefined for unauthenticated users)
 * @returns true if email is in the admin whitelist, false otherwise
 * 
 * @example
 * ```typescript
 * import { isAdmin } from './config/adminConfig';
 * 
 * // Check Firebase user
 * if (isAdmin(auth.currentUser?.email)) {
 *   // Show admin features
 * }
 * ```
 */
export const isAdmin = (email: string | null | undefined): boolean => {
  if (!email) return false;
  return ADMIN_EMAILS.includes(email.toLowerCase());
};

/**
 * Checks if a user email is a tester.
 * Testers get 10x quota but no admin menu access.
 */
export const isTester = (email: string | null | undefined): boolean => {
  if (!email) return false;
  return TESTER_EMAILS.includes(email.toLowerCase());
};

/**
 * Checks if a user email is whitelisted for pilot program access.
 * 
 * @param email - User's email address
 * @returns true if email is in pilot whitelist (or pilot mode is disabled)
 * 
 * @example
 * ```typescript
 * import { isWhitelisted, PILOT_MODE_ENABLED } from './config/adminConfig';
 * 
 * if (PILOT_MODE_ENABLED && !isWhitelisted(user.email)) {
 *   // Show "not authorized" message
 *   return;
 * }
 * ```
 */
export const isWhitelisted = (email: string | null | undefined): boolean => {
  // If pilot mode is disabled, everyone is allowed
  if (!PILOT_MODE_ENABLED) return true;
  
  if (!email) return false;
  return PILOT_WHITELIST.includes(email.toLowerCase());
};

/**
 * Admin configuration object containing all admin-related exports.
 * Provided for default export compatibility.
 */
const adminConfig = {
  ADMIN_EMAILS,
  TESTER_EMAILS,
  PILOT_WHITELIST,
  PILOT_MODE_ENABLED,
  MONETIZATION_MODE,
  RECOMMENDATION_MODE,
  isAdmin,
  isTester,
  isWhitelisted,
} as const;

export default adminConfig;
